import { twMerge } from 'tailwind-merge';

import type { DispatchPlaceUser } from '@/features/dispatch-places/dispatch-places.types';

import { useUsersSection } from './use-users-section';

// ── Role mapping ──────────────────────────────────────────────────────────────

const ROLE_LABELS: Record<string, string> = {
  'owner:all': 'Dueño',
  'owner': 'Dueño',
  'operator:all': 'Operador',
  'operator': 'Operador',
  'manager': 'Gerente',
  'viewer': 'Consultor',
  'admin': 'Administrador',
  'cashier': 'Cajero',
};

/**
 * Returns a human-readable role label for a user.
 * Falls back to "Operador" for punto-kind users without explicit roles,
 * and to the raw role string when no mapping exists.
 */
function resolveRoleLabel(roles: DispatchPlaceUser['roles'], kind: DispatchPlaceUser['kind']): string {
  if (roles.length > 0) {
    return roles.map((role) => ROLE_LABELS[role] ?? role).join(', ');
  }
  if (kind === 'punto') return 'Operador';
  return kind || 'Sin rol';
}

// ── Subcomponents ─────────────────────────────────────────────────────────────

function UserRowSkeleton() {
  return (
    <div className="flex items-center gap-3 py-3">
      <div className="skeleton h-2.5 w-2.5 rounded-full shrink-0" />
      <div className="flex-1 flex flex-col gap-1.5">
        <div className="skeleton h-3.5 w-36 rounded" />
        <div className="skeleton h-3 w-24 rounded" />
      </div>
      <div className="skeleton h-5 w-16 rounded-full" />
    </div>
  );
}

interface UserRowProps {
  user: DispatchPlaceUser;
}

function UserRow(props: UserRowProps) {
  const user = props.user;
  const role_label = resolveRoleLabel(user.roles, user.kind);

  return (
    <div className="flex items-center gap-3 py-3">
      {/* Status indicator */}
      <div
        className={twMerge(
          'h-2.5 w-2.5 rounded-full shrink-0',
          user.active ? 'bg-success' : 'bg-error',
        )}
        title={user.active ? 'Activo' : 'Inactivo'}
        aria-label={user.active ? 'Activo' : 'Inactivo'}
      />

      {/* Name + phone */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold leading-none truncate">{user.name}</p>
        {user.phone && (
          <p className="mt-1 text-xs text-base-content/50 leading-none">{user.phone}</p>
        )}
      </div>

      {/* Role */}
      <span className="badge badge-ghost badge-sm whitespace-nowrap shrink-0">
        {role_label}
      </span>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

interface UsersSectionProps {
  dp_id: string;
}

export function UsersSection(props: UsersSectionProps) {
  const { users, isLoading } = useUsersSection(props.dp_id);

  return (
    <div className="card card-border border-base-300 bg-base-100">
      <div className="card-body gap-4">
        <div className="flex items-center justify-between">
          <h2 className="card-title text-base">Usuarios</h2>
          {!isLoading && (
            <span className="text-xs text-base-content/50">
              {users.length} {users.length === 1 ? 'usuario' : 'usuarios'}
            </span>
          )}
        </div>

        <div className="divide-y divide-base-200">
          {isLoading && (
            <>
              <UserRowSkeleton />
              <UserRowSkeleton />
              <UserRowSkeleton />
            </>
          )}

          {!isLoading && users.length === 0 && (
            <p className="py-6 text-center text-sm text-base-content/50">
              Sin usuarios registrados.
            </p>
          )}

          {!isLoading && users.map((user) => (
            <UserRow key={user.id} user={user} />
          ))}
        </div>
      </div>
    </div>
  );
}
